import type { TikTokVideoFacts } from '@shared/tiktok-video-facts';
import { useEffect, useState } from 'react';

const PROBE_MS = 1500;

type ProbeVideo = HTMLVideoElement & {
  mozHasAudio?: boolean;
  webkitAudioDecodedByteCount?: number;
  audioTracks?: { length: number };
  requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number;
};

/**
 * Reads pixel size, frame rate and sound presence from a video in the browser,
 * for publisher pre-flight. Mora stores none of these. Anything the browser
 * cannot tell is `null`: Firefox and Safari expose different audio signals,
 * and frame rate is sampled from about a second of muted playback, so it is an
 * estimate.
 */
export function useVideoFacts(url: string | null, enabled: boolean): TikTokVideoFacts | null {
  const [facts, setFacts] = useState<TikTokVideoFacts | null>(null);

  useEffect(() => {
    setFacts(null);
    if (!enabled || !url) return;
    let cancelled = false;
    const video = document.createElement('video') as ProbeVideo;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    video.src = url;

    const sampled: { frames: number; first: number | null; last: number | null } = {
      frames: 0,
      first: null,
      last: null,
    };
    const onFrame = (_now: number, meta: { mediaTime: number }) => {
      sampled.frames += 1;
      sampled.first ??= meta.mediaTime;
      sampled.last = meta.mediaTime;
      if (!cancelled) video.requestVideoFrameCallback?.(onFrame);
    };

    const finish = () => {
      if (cancelled) return;
      const span =
        sampled.first !== null && sampled.last !== null ? sampled.last - sampled.first : 0;
      const fps = span > 0.3 && sampled.frames > 2 ? (sampled.frames - 1) / span : null;
      let hasAudio: boolean | null = null;
      if (typeof video.mozHasAudio === 'boolean') hasAudio = video.mozHasAudio;
      else if (typeof video.webkitAudioDecodedByteCount === 'number' && sampled.frames > 0) {
        hasAudio = video.webkitAudioDecodedByteCount > 0;
      } else if (video.audioTracks && typeof video.audioTracks.length === 'number') {
        hasAudio = video.audioTracks.length > 0;
      }
      setFacts({
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        fps,
        hasAudio,
      });
      video.pause();
      video.removeAttribute('src');
      video.load();
    };

    video.addEventListener('loadedmetadata', () => {
      if (cancelled) return;
      video.requestVideoFrameCallback?.(onFrame);
      void video.play().catch(() => undefined);
      window.setTimeout(finish, PROBE_MS);
    });
    video.addEventListener('error', () => {
      if (!cancelled) setFacts({ width: null, height: null, fps: null, hasAudio: null });
    });

    return () => {
      cancelled = true;
      video.pause();
      video.removeAttribute('src');
    };
  }, [url, enabled]);

  return facts;
}
